import type { KeyboardEvent } from 'react';

import type { ToolbarButtons } from '../../../../models';
import { circularFocusHandlerer } from './CiruclarFocusHandlerer';
import { moreToolbarFocusHandlerer } from './MoreToolbarFocusHandlerer';

export const useFocusHandler = () => {
  const handleCircularFocus = ({
    e,
    overflownButtons,
    visibleButtons,
    shouldShowMore,
    moreButton,
  }: {
    e: KeyboardEvent<HTMLDivElement>;
    overflownButtons: ToolbarButtons;
    visibleButtons: ToolbarButtons;
    shouldShowMore: boolean;
    moreButton: HTMLButtonElement | null;
  }) => {
    if (!overflownButtons || !visibleButtons) {
      return;
    }

    const firstButtonDataHook = visibleButtons.getFirstButtonDataHook();
    const lastButtonDataHook = visibleButtons.getLastButtonDataHook();
    if (!firstButtonDataHook || !lastButtonDataHook) {
      return;
    }

    const moreModalFirstButtonDataHook = overflownButtons.getFirstButtonDataHook();
    const moreModalLastButtonDataHook = overflownButtons.getLastButtonDataHook();

    // Nesting is needed here, because if datahooks or that more button element is not present
    // when showing the more modal, then it might break code or logic.
    if (shouldShowMore) {
      if (moreModalFirstButtonDataHook && moreModalLastButtonDataHook && moreButton) {
        circularFocusHandlerer({
          e,
          firstButtonDataHook,
          lastButtonDataHook,
          moreModalFirstButtonDataHook,
          moreModalLastButtonDataHook,
          moreButton,
          isMoreModalOpen: true,
        });
      }

      return;
    }
    circularFocusHandlerer({
      e,
      firstButtonDataHook,
      lastButtonDataHook,
      moreButton,
      isMoreModalOpen: false,
    });
  };

  const handleMoreToolbarFocus = ({
    e,
    overflownButtons,
    visibleButtons,
    shouldShowMore,
    moreButton,
  }: {
    e: KeyboardEvent<HTMLDivElement>;
    overflownButtons: ToolbarButtons;
    visibleButtons: ToolbarButtons;
    shouldShowMore: boolean;
    moreButton: HTMLButtonElement | null;
  }) => {
    const firstButtonDataHook = visibleButtons.getFirstButtonDataHook();

    const moreModalFirstButtonDataHook = overflownButtons.getFirstButtonDataHook();
    const moreModalLastButtonDataHook = overflownButtons.getLastButtonDataHook();

    if (
      !firstButtonDataHook ||
      !moreModalFirstButtonDataHook ||
      !moreModalLastButtonDataHook ||
      !shouldShowMore ||
      !moreButton
    ) {
      return;
    }
    moreToolbarFocusHandlerer({
      e,
      firstButtonDataHook,
      moreModalFirstButtonDataHook,
      moreModalLastButtonDataHook,
      moreButton,
    });
  };

  return {
    handleCircularFocus,
    handleMoreToolbarFocus,
  };
};
